/**
 * stageRoom — the one decision behind "Add accessories", wherever it runs.
 *
 * The browser (`web-app/src/services/stagingQueue.js`) and the
 * `style_room_decor` tool both call this, so the two cannot drift apart: the
 * web plans locally and saves through the room store, the tool plans inside
 * its transaction for the chat agent and the admin debug page.
 *
 * What it does NOT do, on purpose, because the two callers do it differently:
 *   - resolve the style — the tool refuses an unknown pack before opening a
 *     transaction, and keeps that order;
 *   - hydrate and filter the openings — the server uses
 *     `openings/openingSync.withResolvedOpeningsOnly`, the web its twin
 *     `withRenderableOpeningsOnly` (`@shared-lib/openings` is not a web
 *     dependency). Pass a room whose openings carry real geometry: a slim
 *     copy has none, and art would be hung against a window that isn't there.
 *
 * @param {Object} room   hydrated, openings filtered to those with geometry
 * @param {Object} style  a resolved staging pack
 * @param {Object} [opts]
 * @param {() => string} [opts.freshId]  id minter for the persisted props
 * @param {string} [opts.generatedAt]    ISO stamp for provenance
 * @returns {{ kits: string[], decor: Object[], objects: Object[],
 *     stagedHosts: Object[] }
 *   | { refusal: 'not-stageable' }}
 *   `stagedHosts` is the room's full replacement list (`./stagedHosts.js`):
 *   write it whole beside the props, empty included, so a re-stage clears
 *   the last run's.
 */

const { planStagingObjects } = require('./planStagingObjects');
const { stagingKitsFor } = require('./stagingKind');

function stageRoom(room, style, { freshId, generatedAt } = {}) {
  // The kits this room gets: one per area of an open plan, read from the
  // room-kind SET rather than a collapsed primary kind.
  const kits = stagingKitsFor(room);
  if (!kits.length) return { refusal: 'not-stageable' };

  const { decor, objects, stagedHosts } = planStagingObjects(room, style, {
    persistIds: true,
    freshId,
    generatedAt,
  });
  return { kits, decor, objects, stagedHosts };
}

module.exports = { stageRoom };
